import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, FlaskConical, Landmark, Truck, ArrowRight, Star } from "lucide-react";
import { api } from "../lib/api";

export const LabVerified = () => {
  const [certified, setCertified] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    api.get("/products")
      .then((r) => {
        setTotal(r.data.length);
        setCertified(r.data.filter((p) => p.coa && (p.coa.provider === "janoshik" || p.coa.provider === "labdoc")));
      })
      .catch(() => {});
  }, []);

  if (certified.length === 0) return null;

  const purity = (p) => {
    const r = (p.coa.results || []).find((x) => x.substance.toLowerCase() === "purity");
    return r ? r.content : "ID + AMOUNT VERIFIED";
  };

  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="lab-verified-section">
      <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">THIRD-PARTY TESTED</div>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Lab verified. Every batch.
          </h2>
          <p className="mt-4 max-w-xl text-sm text-slate-400 sm:text-base">
            Independently analysed by Janoshik Analytical and partner laboratories. Every certificate carries a unique
            key you can verify yourself on the lab's verification page — copy the key and paste it there.
          </p>
        </div>
        <div className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 font-mono text-[11px] tracking-[0.2em] text-emerald-400" data-testid="lab-verified-count">
          {certified.length} OF {total || 20} BATCHES PUBLISHED
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {certified.slice(0, 6).map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.07 }}
            className="glass rounded-2xl p-6 transition-colors duration-300 hover:border-[#E8E9EB]/30"
            data-testid={`lab-card-${p.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg font-semibold text-white">{p.name}</div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.15em] text-slate-500">{p.coa.sample.toUpperCase()}</div>
              </div>
              <BadgeCheck className="h-5 w-5 shrink-0 text-emerald-400" />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] text-slate-300">{purity(p)}</span>
              <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] text-slate-400">TASK #{p.coa.task_number}</span>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-4">
              <span className="font-mono text-[10px] tracking-[0.15em] text-[#E8E9EB]">KEY {p.coa.verify_code}</span>
              <Link to={`/coa/${p.id}`} data-testid={`lab-report-link-${p.id}`} className="group inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-slate-400 transition-colors duration-200 hover:text-white">
                VIEW REPORT <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const STEPS = [
  { n: "01", icon: FlaskConical, title: "Choose your protocol", text: "Browse twenty bio-engineered formulations, each with published purity data and batch-specific test reports." },
  { n: "02", icon: Landmark, title: "Secure bank transfer", text: "Pay instantly in AUD via Osko/PayID bank transfer. Payment instructions are emailed to you right after checkout." },
  { n: "03", icon: Truck, title: "Cold-chain dispatch", text: "Orders leave our Sydney facility within 24 hours in temperature-protected, discreet packaging." },
];

export const HowItWorks = () => (
  <section className="border-t border-white/5 bg-[#0A0A0C] px-5 py-24 sm:px-8 lg:py-32" data-testid="how-it-works-section">
    <div className="mx-auto max-w-7xl">
      <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">HOW IT WORKS</div>
      <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
        From catalog to cold-chain in three steps
      </h2>
      <div className="mt-16">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="group grid gap-4 border-t border-white/8 py-10 transition-colors duration-300 hover:border-[#E8E9EB]/30 md:grid-cols-[120px_60px_1fr_1.4fr] md:items-center"
            data-testid={`how-step-${s.n}`}
          >
            <span className="font-mono text-sm text-[#6E7683] transition-colors duration-300 group-hover:text-[#E8E9EB]">/{s.n}</span>
            <s.icon className="hidden h-6 w-6 text-[#E8E9EB] md:block" />
            <h3 className="font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">{s.title}</h3>
            <p className="text-sm leading-relaxed text-slate-400 sm:text-base">{s.text}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);


const QUOTES = [
  { quote: "Ordered Monday night, arrived Wednesday morning still cold. The COA key checked out on Janoshik in seconds — that is what sold me.", name: "J.M.", location: "Sydney NSW" },
  { quote: "Clean packaging, clear batch numbers, and support replied within the hour when I had a question about reconstitution.", name: "R.T.", location: "Melbourne VIC" },
  { quote: "Third order now. Consistent quality every time and the lab reports being public on each product is a genuine point of difference.", name: "D.K.", location: "Brisbane QLD" },
];

export const Testimonials = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-28" data-testid="testimonials-section">
    <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">CUSTOMER REVIEWS</div>
    <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
      Trusted by researchers across Australia
    </h2>
    <div className="mt-12 grid gap-6 md:grid-cols-3">
      {QUOTES.map((q, i) => (
        <motion.figure
          key={q.name}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: i * 0.08 }}
          className="glass flex flex-col rounded-2xl p-7 transition-colors duration-300 hover:border-[#E8E9EB]/30"
          data-testid={`testimonial-${i}`}
        >
          <div className="flex gap-1">
            {[...Array(5)].map((_, s) => (
              <Star key={s} className="h-4 w-4 fill-[#E8E9EB] text-[#E8E9EB]" />
            ))}
          </div>
          <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-slate-300">&ldquo;{q.quote}&rdquo;</blockquote>
          <figcaption className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
            <span className="font-mono text-xs tracking-[0.15em] text-white">{q.name} · {q.location}</span>
            <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.15em] text-emerald-400">VERIFIED BUYER</span>
          </figcaption>
        </motion.figure>
      ))}
    </div>
  </section>
);
